import { useState, useRef, useEffect } from "react";
import { ChatCircleDots, PaperPlaneRight, X, Sparkle } from "@phosphor-icons/react";
import { API } from "../lib/api";

export default function AIChat() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Welcome to Point Penta. I'm your AI concierge — ask me about Dubai communities, off-plan launches, yields or what to view this weekend." },
  ]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [session] = useState(() => `chat-${Math.random().toString(36).slice(2)}`);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streaming]);

  async function send() {
    const msg = input.trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const resp = await fetch(`${API}/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: session, message: msg }),
      });
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const chunks = buffer.split("\n\n");
        buffer = chunks.pop() || "";
        for (const c of chunks) {
          if (c.startsWith("data: ")) {
            const payload = c.slice(6);
            if (payload === "[DONE]") continue;
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: (copy[copy.length - 1].content || "") + payload };
              return copy;
            });
          }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Sorry — please try again or contact our team." };
        return copy;
      });
    } finally { setStreaming(false); }
  }

  return (
    <>
      <button
        data-testid="ai-chat-toggle"
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-24 right-6 z-40 w-14 h-14 rounded-full bg-[#001588] text-white flex items-center justify-center shadow-xl hover:scale-105 hover:bg-black transition-all"
        aria-label="Open AI concierge"
      >
        {open ? <X size={22} weight="bold" /> : <ChatCircleDots size={24} weight="fill" />}
      </button>

      {open && (
        <div data-testid="ai-chat-panel" className="fixed bottom-40 right-6 z-40 w-[92vw] sm:w-[420px] h-[min(560px,calc(100vh-260px))] max-h-[calc(100vh-240px)] bg-white border border-[#e5e7eb] flex flex-col shadow-2xl">
          <div className="px-5 py-4 border-b border-[#e5e7eb] flex items-center gap-3 bg-[#ffffff]">
            <Sparkle size={18} weight="fill" className="text-[#001588]" />
            <div className="flex flex-col leading-tight">
              <span className="font-serif text-lg text-[#050d19]">Penta Concierge</span>
              <span className="text-[10px] tracking-[0.24em] uppercase text-neutral-500">AI advisor · claude sonnet 4.5</span>
            </div>
          </div>
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
            {messages.map((m, i) => (
              <div key={i}
                className={`text-sm leading-relaxed max-w-[92%] ${
                  m.role === "user"
                    ? "self-end ml-auto bg-[#001588] text-white px-4 py-2.5"
                    : "text-neutral-800 border-l-2 border-[#001588] pl-3"
                }`}
                data-testid={`ai-msg-${m.role}`}
              >
                {m.content || (m.role === "assistant" && streaming ? "…" : "")}
              </div>
            ))}
          </div>
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-3 border-t border-[#e5e7eb] flex items-center gap-2">
            <input value={input} onChange={(e) => setInput(e.target.value)} disabled={streaming}
              placeholder="Ask about a community, price or off-plan…"
              data-testid="ai-chat-input"
              className="flex-1 bg-white border border-[#e5e7eb] px-4 py-2.5 text-sm text-[#050d19] focus:outline-none focus:border-[#001588]" />
            <button type="submit" disabled={streaming} data-testid="ai-chat-send"
              className="w-10 h-10 bg-[#001588] text-white flex items-center justify-center hover:bg-black disabled:opacity-50">
              <PaperPlaneRight size={16} weight="fill" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
